import {
    forwardRef,
    ReactElement,
    useCallback,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';
import { CircleAlertIcon, CircleXIcon, InfoIcon } from 'lucide-react';

import styles from './toast.module.scss';

type ToastType = 'info' | 'error' | 'warning';

type ShowToastParams = { text: string; type: ToastType };

type ToastRef = {
    show: (params: ShowToastParams) => void;
};

type ToastRefObj = {
    current: ToastRef | null;
};

type ToastProps = {
    visible: boolean;
    text?: string;
    type?: ToastType;
};

const ToastUI = ({ visible, text, type }: ToastProps) => {
    const renderIcon: (type: ToastType) => ReactElement = useCallback(
        (type) => {
            switch (type) {
                case 'info':
                default:
                    return <InfoIcon size={20} />;
                case 'error':
                    return <CircleXIcon size={20} />;
                case 'warning':
                    return <CircleAlertIcon size={20} />;
            }
        },
        []
    );

    return (
        <div
            className={classnames({
                [styles.container]: true,
                [styles.shown]: visible,
            })}
        >
            <div className={styles.toast}>
                {type && renderIcon(type)}
                {text && <span>{text}</span>}
            </div>
        </div>
    );
};

const useToast = () => {
    const [text, setText] = useState<string | undefined>(undefined);
    const [visible, setVisible] = useState(false);
    const [type, setType] = useState<ToastType | undefined>(undefined);

    return {
        text,
        visible,
        type,
        show: ({ text, type }: ShowToastParams) => {
            setVisible(true);
            setText(text);
            setType(type);
            setTimeout(() => {
                setVisible(false);
                setText(undefined);
                setType(undefined);
            }, 5000);
        },
    };
};

const ToastWithRef = forwardRef<ToastRef>((_, ref) => {
    const { visible, text, type, show } = useToast();

    useImperativeHandle(
        ref,
        useCallback(() => ({ show }), [show])
    );

    return <ToastUI visible={visible} text={text} type={type} />;
});

let refs: ToastRefObj[] = [];

function getRef(): ToastRef | null {
    return refs[0].current;
}

const Toast = () => {
    const toastRef = useRef<ToastRef | null>(null);

    const setRef = useCallback((ref: ToastRef | null) => {
        if (ref) {
            toastRef.current = ref;
            refs.push({ current: ref });
        } else {
            refs = refs.filter((r) => r.current !== toastRef.current);
        }
    }, []);

    return <ToastWithRef ref={setRef} />;
};

Toast.show = (params: ShowToastParams) => {
    getRef()?.show(params);
};

export { Toast };
